import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Avatar,
  Typography,
  Stack,
  InputAdornment,
  Switch,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  Business,
  Save,
  Cancel,
  CloudUpload,
  LocationOn,
  Language,
  Phone,
  Email,
  Refresh,
  ContentCopy,
  Public,
  WhatsApp,
  Smartphone,
  Settings,
  AutoFixHigh,
  OpenInNew,
} from '@mui/icons-material';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import { useCounty, useUpdateCounty } from '../../hooks';
import { generateVerifyToken } from '../../utils/tokens';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { colors } from '../../theme/theme';
import { superAdminService } from '../../services/superadmin.service';

type Channel = 'portal' | 'mobile' | 'whatsapp';
const CHANNEL_META: Record<Channel, { title: string; description: string; icon: React.ReactNode }> = {
  portal: { title: 'Citizen Portal', description: 'Web portal for browsing services and paying bills', icon: <Public /> },
  mobile: { title: 'Mobile App', description: 'Branded mobile app for iOS and Android', icon: <Smartphone /> },
  whatsapp: { title: 'WhatsApp', description: 'Conversational access via the WhatsApp Business number', icon: <WhatsApp /> },
};

interface EditFormState {
  name: string;
  code: string;
  subdomain: string;
  office_location: string;
  website: string;
  phone: string;
  email: string;
}

const emptyForm: EditFormState = {
  name: '',
  code: '',
  subdomain: '',
  office_location: '',
  website: '',
  phone: '',
  email: '',
};

// Portal multi-tenancy — mirror CreateCountyPage / backend County::subdomainRules().
const PORTAL_BASE_DOMAIN = process.env.REACT_APP_PORTAL_BASE_DOMAIN || 'kauntee.com';

export const EditCountyPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const countyId = parseInt(id || '0');

  const { county, isLoading } = useCounty(countyId);
  const updateCounty = useUpdateCounty();
  const qc = useQueryClient();

  // Touchpoints query + mutations
  const touchpointsQuery = useQuery({
    queryKey: ['superadmin', 'county', countyId, 'touchpoints'],
    queryFn: () => superAdminService.getCountyTouchpoints(countyId),
    enabled: !!countyId,
  });
  const channels = useMemo<Array<{ channel: Channel; exists: boolean; touchpoint: any }>>(
    () => ((touchpointsQuery.data as any)?.data ?? []) as any,
    [touchpointsQuery.data],
  );

  const upsertTouchpoint = useMutation({
    mutationFn: ({ channel, payload }: { channel: Channel; payload: Record<string, any> }) =>
      superAdminService.upsertCountyTouchpoint(countyId, channel, payload as any),
    onSuccess: (res) => {
      const creds = (res as any).credentials;
      if (creds) {
        setShownCredentials({ username: creds.username, key: creds.key });
      } else {
        showSnackbar('Touchpoint updated', 'success');
      }
      qc.invalidateQueries({ queryKey: ['superadmin', 'county', countyId, 'touchpoints'] });
    },
    onError: (err: any) => {
      showSnackbar(err?.response?.data?.message || 'Failed to update touchpoint', 'error');
    },
  });

  const [shownCredentials, setShownCredentials] = useState<{ username: string; key: string } | null>(null);

  // WhatsApp creds modal — opened when the superadmin first enables WhatsApp on this county.
  const [waCredsOpen, setWaCredsOpen] = useState(false);
  const [waCreds, setWaCreds] = useState({
    meta_phone_number_id: '',
    display_phone_number: '',
    meta_waba_id: '',
    meta_access_token: '',
    meta_app_secret: '',
    meta_verify_token: '',
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showSnackbar('Copied to clipboard', 'success');
  };

  const [form, setForm] = useState<EditFormState>(emptyForm);
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (county) {
      setForm({
        name: county.name || '',
        code: county.code || '',
        subdomain: (county as any).subdomain || '',
        office_location: county.county_details?.office_location || '',
        website: county.county_details?.website || '',
        phone: (county.county_details as any)?.phone || '',
        email: (county.county_details as any)?.email || '',
      });
      setLogoPreview(county.county_details?.logo || '');
    }
  }, [county]);

  const handleChange = (field: keyof EditFormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showSnackbar('Please select an image file', 'error');
      return;
    }
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    setFormError('');
    setValidationErrors({});

    if (!form.name.trim() || !form.code.trim()) {
      setFormError('County name and code are required.');
      return;
    }

    try {
      await updateCounty.mutateAsync({
        id: countyId,
        data: {
          name: form.name,
          code: form.code,
          ...(form.subdomain ? { subdomain: form.subdomain } : {}),
          office_location: form.office_location || undefined,
          website: form.website || undefined,
          ...(logo ? { logo } : {}),
          // backend also accepts these via the details key — service merges them into countyDetails
          ...(form.phone || form.email
            ? ({
                details: {
                  phone: form.phone || undefined,
                  email: form.email || undefined,
                  office_location: form.office_location || undefined,
                  website: form.website || undefined,
                },
              } as any)
            : {}),
        },
      });
      showSnackbar('County updated successfully', 'success');
      navigate(`/superadmin/counties/${countyId}`);
    } catch (err: any) {
      const errorData = err?.response?.data;
      if (errorData?.errors && typeof errorData.errors === 'object') {
        setValidationErrors(errorData.errors);
        setFormError(errorData.message || 'Validation failed');
      } else {
        setFormError(errorData?.message || 'Failed to update county');
      }
      showSnackbar('Failed to update county', 'error');
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (!county) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Typography>County not found</Typography>
        </Box>
      </AdminLayout>
    );
  }

  const fieldError = (field: string) => validationErrors[field]?.[0];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title={`Edit ${county.name}`}
          subtitle="Update county details and contact information"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'Counties', path: '/superadmin/counties' },
            { label: county.name, path: `/superadmin/counties/${countyId}` },
            { label: 'Edit' },
          ]}
        />

        {formError && (
          <Alert severity="error" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
            {formError}
          </Alert>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${colors.grey[200]}`,
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: colors.success.dark, mb: 1 }}>
              County Logo
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a new logo or keep the existing one
            </Typography>
            <Stack direction="row" spacing={3} alignItems="center">
              <Avatar
                src={logoPreview}
                sx={{
                  width: 96,
                  height: 96,
                  bgcolor: colors.grey[100],
                  border: `2px solid ${colors.grey[200]}`,
                }}
              >
                {!logoPreview && <Business sx={{ fontSize: 48, color: colors.grey[400] }} />}
              </Avatar>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                sx={{ borderColor: colors.success.main, color: colors.success.dark }}
              >
                {logo ? 'Change Logo' : 'Upload New Logo'}
                <input type="file" accept="image/*" hidden onChange={handleLogoChange} />
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${colors.grey[200]}`,
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: colors.success.dark, mb: 3 }}>
              County Information
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label="County Name"
                  value={form.name}
                  onChange={handleChange('name')}
                  error={!!fieldError('name')}
                  helperText={fieldError('name')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Business fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label="County Code"
                  value={form.code}
                  onChange={handleChange('code')}
                  error={!!fieldError('code')}
                  helperText={fieldError('code')}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Portal Subdomain"
                  value={form.subdomain}
                  onChange={(e) => {
                    const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                    setForm((prev) => ({ ...prev, subdomain: cleaned }));
                    if (validationErrors.subdomain) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.subdomain;
                        return next;
                      });
                    }
                  }}
                  error={!!fieldError('subdomain')}
                  helperText={
                    fieldError('subdomain') ||
                    (form.subdomain
                      ? `⚠ Changing this changes the live portal URL — currently https://${form.subdomain}.${PORTAL_BASE_DOMAIN}`
                      : 'No portal subdomain assigned yet')
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Language fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        .{PORTAL_BASE_DOMAIN}
                        {form.subdomain && (
                          <IconButton
                            size="small"
                            component="a"
                            href={`https://${form.subdomain}.${PORTAL_BASE_DOMAIN}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ ml: 0.5 }}
                            aria-label="Open county portal"
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Office Location"
                  value={form.office_location}
                  onChange={handleChange('office_location')}
                  error={!!fieldError('office_location')}
                  helperText={fieldError('office_location')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Website"
                  placeholder="https://example.go.ke"
                  value={form.website}
                  onChange={handleChange('website')}
                  error={!!fieldError('website') || !!fieldError('details.website')}
                  helperText={fieldError('website') || fieldError('details.website')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Language fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Contact Phone"
                  value={form.phone}
                  onChange={handleChange('phone')}
                  error={!!fieldError('details.phone')}
                  helperText={fieldError('details.phone')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Contact Email"
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  error={!!fieldError('details.email')}
                  helperText={fieldError('details.email')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Touchpoints */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${colors.grey[200]}`,
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: colors.success.dark, mb: 1 }}>
              Access Channels
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Channels citizens can use to reach this county. Enabling a channel generates an API key for it; you can regenerate it any time.
            </Typography>
            {touchpointsQuery.isLoading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
            ) : (
              <Stack spacing={2}>
                {channels.map(({ channel, exists, touchpoint }) => {
                  const meta = CHANNEL_META[channel];
                  const active = !!(touchpoint && touchpoint.status === 'active');
                  const pendingForRow = upsertTouchpoint.isPending && (upsertTouchpoint.variables as any)?.channel === channel;
                  return (
                    <Box
                      key={channel}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        border: `1px solid ${active ? colors.success.main : colors.grey[200]}`,
                        borderLeft: `4px solid ${active ? colors.success.main : colors.grey[300]}`,
                        borderRadius: 2,
                        bgcolor: active ? `${colors.success.main}08` : 'transparent',
                      }}
                    >
                      <Avatar sx={{ bgcolor: active ? colors.success.main : colors.grey[300] }}>
                        {meta.icon}
                      </Avatar>
                      <Box flex={1}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="subtitle1" fontWeight={700}>{meta.title}</Typography>
                          {!exists && <Chip label="Not enabled" size="small" color="default" />}
                          {exists && (
                            <Chip
                              label={active ? 'Active' : 'Inactive'}
                              size="small"
                              color={active ? 'success' : 'warning'}
                            />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {meta.description}
                        </Typography>
                        {touchpoint?.username && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            username: {touchpoint.username}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip title={exists ? (active ? 'Disable channel' : 'Enable channel') : 'Enable channel (generates key)'}>
                          <Switch
                            checked={active}
                            disabled={pendingForRow}
                            onChange={(e) => {
                              const turningOn = e.target.checked;
                              // For WhatsApp: when enabling for the first time (no existing touchpoint),
                              // open the credentials modal instead of creating an empty unusable touchpoint.
                              if (channel === 'whatsapp' && turningOn && !exists) {
                                setWaCreds({
                                  meta_phone_number_id: '',
                                  display_phone_number: '',
                                  meta_waba_id: '',
                                  meta_access_token: '',
                                  meta_app_secret: '',
                                  meta_verify_token: '',
                                });
                                setWaCredsOpen(true);
                                return;
                              }
                              upsertTouchpoint.mutate({
                                channel,
                                payload: { status: turningOn ? 'active' : 'inactive' },
                              });
                            }}
                            color="success"
                          />
                        </Tooltip>
                        {exists && channel === 'whatsapp' && (
                          <Tooltip title="Edit WhatsApp credentials">
                            <span>
                              <IconButton
                                disabled={pendingForRow}
                                onClick={() => {
                                  setWaCreds({
                                    meta_phone_number_id: touchpoint?.meta_phone_number_id || '',
                                    display_phone_number: touchpoint?.display_phone_number || '',
                                    meta_waba_id: touchpoint?.meta_waba_id || '',
                                    meta_access_token: '',
                                    meta_app_secret: '',
                                    meta_verify_token: touchpoint?.meta_verify_token || '',
                                  });
                                  setWaCredsOpen(true);
                                }}
                              >
                                <Settings />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {exists && (
                          <Tooltip title="Regenerate API key">
                            <span>
                              <IconButton
                                disabled={pendingForRow}
                                onClick={() =>
                                  upsertTouchpoint.mutate({ channel, payload: { regenerate: true } })
                                }
                              >
                                <Refresh />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Cancel />}
            onClick={() => navigate(`/superadmin/counties/${countyId}`)}
            disabled={updateCounty.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={updateCounty.isPending ? <CircularProgress size={18} color="inherit" /> : <Save />}
            onClick={handleSubmit}
            disabled={updateCounty.isPending}
            sx={{
              background: `linear-gradient(135deg, ${colors.success.main} 0%, ${colors.success.dark} 100%)`,
            }}
          >
            {updateCounty.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {/* One-time credentials display: new key (create or regenerate) — shown once, never re-fetchable */}
      <Dialog open={!!shownCredentials} onClose={() => setShownCredentials(null)} maxWidth="sm" fullWidth>
        <DialogTitle>API key generated</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is the <strong>only time</strong> the key will be shown. Copy it now and store it securely.
          </Alert>
          {shownCredentials && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Username</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{shownCredentials.username}</Typography>
                  <IconButton size="small" onClick={() => copyToClipboard(shownCredentials.username)}><ContentCopy fontSize="small" /></IconButton>
                </Stack>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">API Key</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{shownCredentials.key}</Typography>
                  <IconButton size="small" onClick={() => copyToClipboard(shownCredentials.key)}><ContentCopy fontSize="small" /></IconButton>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setShownCredentials(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp credentials modal — capture per-county Meta config */}
      <Dialog open={waCredsOpen} onClose={() => setWaCredsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>WhatsApp credentials</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>Configure these in Meta first</Typography>
            <Typography variant="body2">
              In your Meta App → WhatsApp → Configuration, set:<br />
              <strong>Callback URL:</strong> <code>{`${window.location.protocol}//${window.location.hostname}/api/whatsapp/webhook`}</code><br />
              <strong>Verify token:</strong> whatever you enter below — must match exactly.
            </Typography>
          </Alert>
          <Stack spacing={2}>
            <TextField label="Meta Phone Number ID" required value={waCreds.meta_phone_number_id}
              onChange={(e) => setWaCreds({ ...waCreds, meta_phone_number_id: e.target.value })}
              helperText="From Meta → WhatsApp → API Setup" />
            <TextField label="Display Phone Number" value={waCreds.display_phone_number}
              onChange={(e) => setWaCreds({ ...waCreds, display_phone_number: e.target.value })}
              placeholder="+254700000000" />
            <TextField label="WhatsApp Business Account ID" value={waCreds.meta_waba_id}
              onChange={(e) => setWaCreds({ ...waCreds, meta_waba_id: e.target.value })} />
            <TextField
              label="Webhook Verify Token"
              required
              value={waCreds.meta_verify_token}
              onChange={(e) => setWaCreds({ ...waCreds, meta_verify_token: e.target.value })}
              helperText="Generate one and paste the same value into Meta — they must match"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Generate random token">
                      <IconButton
                        size="small" edge="end"
                        onClick={() => setWaCreds({ ...waCreds, meta_verify_token: generateVerifyToken() })}
                      >
                        <AutoFixHigh fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <TextField label="Access Token (leave blank to keep current)" multiline minRows={2}
              value={waCreds.meta_access_token}
              onChange={(e) => setWaCreds({ ...waCreds, meta_access_token: e.target.value })} />
            <TextField label="App Secret (leave blank to keep current)" type="password"
              value={waCreds.meta_app_secret}
              onChange={(e) => setWaCreds({ ...waCreds, meta_app_secret: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaCredsOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success"
            onClick={() => {
              const payload: Record<string, any> = {
                status: 'active',
                meta_phone_number_id: waCreds.meta_phone_number_id || undefined,
                display_phone_number: waCreds.display_phone_number || undefined,
                meta_waba_id: waCreds.meta_waba_id || undefined,
                meta_verify_token: waCreds.meta_verify_token || undefined,
              };
              if (waCreds.meta_access_token) payload.meta_access_token = waCreds.meta_access_token;
              if (waCreds.meta_app_secret) payload.meta_app_secret = waCreds.meta_app_secret;
              upsertTouchpoint.mutate({ channel: 'whatsapp', payload }, {
                onSuccess: () => setWaCredsOpen(false),
              });
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};
